import HeaderSection from '@/components/Header/HeaderSection.vue';
import tile_card from '@/components/tile_card/TIleCardView.vue';
import DetailsCard from '@/components/Details_card/DetailCardView.vue';
import OverviewSummary from '@/components/OverviewSummary/OverviewSummary.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import WelcomeBack from '@/components/WelcomeBack/WelcomeBack.vue';
import PropertyOverview from '@/components/PropertyOverview/PropertyOverview.vue';
import EmergencyContactMap from '@/components/EmergencyContactMap/EmergencyContactMap.vue';
import QuickActions from '@/components/QuickActions/QuickActions.vue';
import ServiceProvider from '@/components/ServiceProvider/ServiceProvider.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import PropertySidebar from '@/components/PropertySidebar/PropertySidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { get_token } from '@/router';
import getTilesConfiguration from '@/data/tiles';
import {
  global_executive_overview_dashboard_id,
  global_impact_exchange_rate_engine_dashboard_id,
  global_fund_performance_capital_recycling_dashboard_id,
  global_real_time_operations_asset_control_dashboard_id,
  role_dashboards
} from '@/config/dashboardIds';
import ShiftSchedule from '@/components/ShiftSchedule/ShiftSchedule.vue';
import RecentDocuments from '@/components/RecentDocuments/RecentDocuments.vue';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';
import SecurityDemographicsCompetition from '@/components/SecurityDemographicsCompetition/SecurityDemographicsCompetition.vue';
import CalculatorInput from '@/components/CalculatorInput/CalculatorInput.vue';
import ProjectedOutcomes from '@/components/ProjectedOutcomes/ProjectedOutcomes.vue';

// Environment variables
const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;
const superset_sdk_url = process.env.VUE_APP_SUPERSET_SDK_URL;

// Superset Config
const SUPERSET_CONFIG = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: global_executive_overview_dashboard_id
  }
};

const AUTH_CONFIG = {
  guestTokenUrl: guest_token_url
};

const MAIN_BREADCRUMB_TITLE = "Gauteng Water Command Centre";

// Token Cache
const TOKEN_CACHE = {
  tokens: new Map(),

  get(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && Date.now() < cached.expiresAt) return cached.token;
    if (cached) this.tokens.delete(dashboardId);
    return null;
  },

  set(dashboardId, token, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() { this.tokens.clear(); },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt) this.tokens.delete(dashboardId);
    }
  }
};

function safeParseMaybeDoubleEncodedJSON(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

export default {
  name: 'DashboardView',

  components: {
    tile_card,
    HeaderSection,
    DetailsCard,
    OverviewSummary,
    PortfolioSuperSetChart,
    WelcomeBack,
    PropertyOverview,
    EmergencyContactMap,
    QuickActions,
    ServiceProvider,
    PortfolioSidebar,
    PropertySidebar,
    BolideAssistant,
    RecentDocuments,
    ShiftSchedule,
    BolideLoader,
    SecurityDemographicsCompetition,
    CalculatorInput,
    ProjectedOutcomes
  },

  data() {
    return ({
      // View State
      currentView: 'main',
      current_dashboard_type: 'global',
      active_menu_item: 'executive-overview',
      role_key: 'fund_manager',

      // Data
      tiles: [],
      breadcrumb: [],
      selected_property: null,

      // UI State
      iframeLoading: false,
      iframeError: false,
      show_all: false,
      rightPanelCollapsed: false,
      isMobile: false,
      windowWidth: 0,

      // Superset State
      sdkLoaded: false,
      sdkConfigured: false,
      initializationComplete: false,
      authToken: null,
      embeddedDashboards: new Set(),
      tokenRefreshInProgress: false,
      showAssistant: false,
      sidebar_collapsed: false,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false,
      calculator_inputs: null,
      projected_outcomes: {
        assets: 0,
        jobs_expected: 0,
        jobs_verified: 0,
        revenue: "R0",
        recycled: "R0",
        bonus: "R0",
        survival_rate: "0%",
        cost_per_job: "R0",
        break_even_period: "0mo"
      },
      // Dashboard Mappings
      global_dashboards: ({
        'executive-overview': global_executive_overview_dashboard_id,
        'impact-exchange-rate-engine': global_impact_exchange_rate_engine_dashboard_id,
        'fund-performance-capital-recycling': global_fund_performance_capital_recycling_dashboard_id,
        'real-time-operations-asset-control': global_real_time_operations_asset_control_dashboard_id
      }),
      role_dashboards: role_dashboards,

      // Property dashboard mapping - Universal dashboards (fallback)
      property_dashboards: {},

      // Property-specific dashboard mappings (now loaded from config)
      property_specific_dashboards: {},
    });
  },

  computed: {
    currentCrimeStats() {
      if (!this.selected_property || !this.selected_property.crimeStats) {
        return {};
      }
      return this.selected_property.crimeStats;
    },

    visible_tiles() {
      return this.show_all ? this.tiles : this.tiles.slice(0, 3);
    },

    showRightPanel() {
      return this.isMobile || !this.rightPanelCollapsed;
    },

    current_dashboard_id() {
      if (this.current_dashboard_type === 'global') {
        const role_dashboards_map = this.role_dashboards[this.role_key] || this.role_dashboards.fund_manager;
        return role_dashboards_map[this.active_menu_item] || role_dashboards_map.default || global_executive_overview_dashboard_id;
      }

      return global_executive_overview_dashboard_id;
    },

    is_property_view() {
      return this.current_dashboard_type === 'property' && this.selected_property;
    },

    /**
     * -----------------------------------------------
     * NEW COMPUTED: First Management Contact
     * -----------------------------------------------
     */
    management_contact() {
      const card = this.selected_property?.tileCards?.find(
        tileCard =>
          tileCard.type === "details" &&
          (
            tileCard.title?.toLowerCase().includes("management") ||
            tileCard.title?.toLowerCase().includes("on-site")
          )
      );

      if (!card) return null;

      // ✅ NEW STRUCTURE (contacts array)
      if (Array.isArray(card.contacts) && card.contacts.length) {
        const preferred =
          card.contacts.find(c =>
            (c.role || "").toLowerCase().includes("centre manager")
          ) ||
          card.contacts.find(c =>
            (c.status || "").toLowerCase() === "on-site"
          ) ||
          card.contacts[0];

        return ({
          full_name: preferred?.full_name || "N/A",
          phone: preferred?.phone || "N/A",
          email: preferred?.email || "N/A",
          role: preferred?.role || "Centre Manager"
        });
      }

      // ✅ OLD STRUCTURE fallback
      const managementDetails = card.details;
      if (!managementDetails) return null;

      return ({
        full_name: managementDetails.hours?.split(":")[0]?.trim() || "N/A",
        phone: managementDetails.phone || "N/A",
        email: managementDetails.email || "N/A",
        role: "Centre Manager"
      });
    }

  },


  mounted() {
    document.documentElement.classList.add("dashboard-scroll-lock");
    document.body.classList.add("dashboard-scroll-lock");
    window.addEventListener("resize", this.handle_resize);

    this.set_role_dashboards();

    this.is_loading = true;
    this.loader_start_time = Date.now();  // Record loader start moment

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    // Fallback if superset never loads
    setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000); // 10s fallback

    // 4️⃣ Load tiles immediately (from external file)
    this.tiles = getTilesConfiguration().map(tile => ({
      ...tile,
      is_live: tile.id.includes("(Live)")
    }));

    // 5️⃣ Setup responsiveness
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // 6️⃣ Set initial breadcrumb
    this.breadcrumb = [{ id: 'main', title: MAIN_BREADCRUMB_TITLE }];

    // 7️⃣ Token cleanup
    this.tokenCleanupInterval = setInterval(() => {
      TOKEN_CACHE.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    document.documentElement.classList.remove("dashboard-scroll-lock");
    document.body.classList.remove("dashboard-scroll-lock");
    window.removeEventListener("resize", this.handle_resize);

    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    set_role_dashboards() {
      const stored_role = sessionStorage.getItem('role') || '';
      const normalized_role = stored_role.toString().trim().toLowerCase().replace(/\s+/g, '_');

      if (Object.prototype.hasOwnProperty.call(this.role_dashboards, normalized_role)) {
        this.role_key = normalized_role;
      } else {
        this.role_key = 'fund_manager';
      }

      const role_dashboards_map = this.role_dashboards[this.role_key] || this.role_dashboards.fund_manager;
      if (!Object.prototype.hasOwnProperty.call(role_dashboards_map, this.active_menu_item)) {
        const first_key = Object.keys(role_dashboards_map).find(key => key !== 'default');
        this.active_menu_item = first_key || this.active_menu_item;
      }
    },
    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handle_resize() {
      this.is_mobile = window.innerWidth < 768;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    open_sidebar() {
      this.mobile_sidebar_open = true;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },
    isCustomView(menuItem) {
      return ['impact-calculator'].includes(menuItem);
    },
    formatBreadcrumbTitle(value) {
      return String(value || "")
        .replaceAll("-", " ")
        .split(" ")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    handleCalculatorInputsChanged(payload) {
      this.calculator_inputs = payload;
      this.projected_outcomes = this.calculateProjectedOutcomes(payload);
    },

    parseProvincePercent(label) {
      // "Western Cape (98%)" -> 98
      const match = String(label || "").match(/\((\d+)%\)/);
      return match ? Number(match[1]) : 90;
    },

    formatCurrency(amount) {
      const n = Number(amount || 0);
      if (!isFinite(n)) return "R0";
      // compact-ish like your UI: R26.8M / R175K
      if (n >= 1_000_000) return `R${(n / 1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `R${Math.round(n / 1_000)}K`;
      return `R${Math.round(n)}`;
    },

    sumCohortBonusPercent(enabled_cohorts = []) {
      const valid_keys = Object.freeze(["youth", "women", "pwd"]);

      const map = Object.freeze({
        youth: 20,
        women: 15,
        pwd: 25,
      });

      return (enabled_cohorts || []).reduce((sum, key) => {

        if (valid_keys.includes(String(key))) {
          const bonus = parseInt(map[String(key)], 10); // base 10, force number
          return sum + (Number.isFinite(bonus) ? bonus : 0);
        }
        return sum;
      }, 0);
    },
    normalizeMixTotal(mix) {
      const total = Object.values(mix || {}).reduce((s, v) => s + Number(v || 0), 0);
      if (!total) return { mix: { ...(mix || {}) }, total: 0, factor: 0 };
      // factor makes calculations stable even if user doesn’t equal 100
      return ({ mix: { ...(mix || {}) }, total, factor: 100 / total });
    },

    calculateProjectedOutcomes(payload) {
      const investment = Number((payload && payload.investment_amount) || 0);
      const provincePercent = this.parseProvincePercent((payload && payload.selected_province) || "");
      const cohortBonusPercent = this.sumCohortBonusPercent((payload && payload.enabled_cohorts) || []);
      const normalized = this.normalizeMixTotal((payload && payload.asset_mix) || {});
      const mix = normalized.mix;
      const factor = normalized.factor;

      const valid_keys = Object.freeze([
        "food_security",
        "digital_inclusion",
        "creative_economy",
        "ngo_digitization",
      ]);

      const weights = Object.freeze({
        food_security: 1.05,
        digital_inclusion: 1.0,
        creative_economy: 0.95,
        ngo_digitization: 0.9,
      });

      // Weighted "impact efficiency" from asset mix
      let efficiency = 0;

      valid_keys.forEach((k) => {
        const raw = parseInt(mix[String(k)], 10);
        const amount = Number.isFinite(raw) ? raw : 0;
        const pct = amount * factor; // normalized to 100
        efficiency += (pct / 100) * parseFloat(weights[String(k)]);
      });

      // survival base from province + small cohort boost + efficiency influence
      const survivalRaw =
        provincePercent + (cohortBonusPercent * 0.15) + ((efficiency - 1) * 12);

      const survival = Math.max(50, Math.min(99, Math.round(survivalRaw)));

      // bonus aligns with screenshot style (20+15=35 => 3.5% of investment)
      const bonusRate = cohortBonusPercent / 1000; // 35 -> 0.035
      const bonusValue = investment * bonusRate;

      // Jobs: base cost per job adjusted by survival + efficiency
      const baseCostPerJob = 1200; // tune later
      const adjustedCostPerJob = baseCostPerJob / (efficiency * (survival / 100));

      const jobsExpected = Math.max(0, Math.round(investment / adjustedCostPerJob));
      const jobsVerified = Math.max(0, Math.round(jobsExpected * (survival / 100)));

      const exchangeRateValue = jobsVerified > 0 ? (investment / jobsVerified) : 0;

      // Assets: a “unit count” proxy (tune later)
      const assets = Math.max(0, Math.round((investment / 28000) * efficiency));

      // Revenue proxy (tune later)
      const revenueValue = investment * (5.0 * efficiency); // ~5x
      const recycledValue = revenueValue * 0.12; // 12% recycled (tune)

      const costPerJob = jobsVerified > 0 ? (investment / jobsVerified) : investment;

      // break-even (months) proxy
      const monthlyNet = Math.max(1, (recycledValue + bonusValue) / 12);
      const breakEvenMonths = Math.max(3, Math.round(investment / monthlyNet));

      return ({
        assets,
        jobs_expected: jobsExpected,
        jobs_verified: jobsVerified,
        revenue: this.formatCurrency(revenueValue),
        recycled: this.formatCurrency(recycledValue),
        bonus: this.formatCurrency(bonusValue),
        survival_rate: survival + "%",
        cost_per_job: this.formatCurrency(costPerJob),
        break_even_period: breakEvenMonths + "mo",
        exchange_rate: this.formatCurrency(exchangeRateValue),
      });
    },
    // ========== DASHBOARD INITIALIZATION ==========
    async initializeDashboard() {
      try {
        this.iframeLoading = true;
        this.iframeError = false;
        this.authToken = get_token();

        if (!this.authToken) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();
        await this.embedDashboard('dashboard-main', this.current_dashboard_id);

        // Superset is ready → hide loader
        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          // Show loader for at least min time
          this.is_loading = false;
        } else {
          // Add delay to complete minimum duration
          const remaining = this.min_loader_time - elapsed;
          setTimeout(() => {
            this.is_loading = false;
          }, remaining);
        }

        this.initializationComplete = true;

      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    async fetchGuestToken(dashboardId = null, { allowMainDashboardFallback = false, forceRefresh = false } = {}) {
      const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;

      if (this.tokenRefreshInProgress) {
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.tokenRefreshInProgress) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        const cachedToken = !forceRefresh ? TOKEN_CACHE.get(targetDashboardId) : null;
        if (cachedToken) return { token: cachedToken };
      }

      this.tokenRefreshInProgress = true;

      try {
        if (forceRefresh) {
          TOKEN_CACHE.tokens.delete(targetDashboardId);
        }

        const cachedToken = !forceRefresh ? TOKEN_CACHE.get(targetDashboardId) : null;
        if (cachedToken) return { token: cachedToken };

        const response = await fetch(AUTH_CONFIG.guestTokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${this.authToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'dws' })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Guest token request failed: ${response.status}, ${errorText}`);
        }

        const rawResponse = await response.text();
        const data = safeParseMaybeDoubleEncodedJSON(rawResponse);

        if (data.status === 'success' && data.data && data.data.token) {
          TOKEN_CACHE.set(targetDashboardId, data.data.token);
          return data.data;
        } else {
          throw new Error(data.message || 'Failed to get guest token');
        }
      } catch (error) {
        if (allowMainDashboardFallback &&
          targetDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main) {
          return this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main, { forceRefresh });
        }

        throw new Error(`Failed to fetch guest token: ${error.message}`);
      } finally {
        this.tokenRefreshInProgress = false;
      }
    },

    async initializeSupersetSDK() {
      if (typeof window.supersetEmbeddedSdk === 'undefined') await this.loadSupersetSDK();
      this.sdkLoaded = true;
      return true;
    },

    loadSupersetSDK() {
      return new Promise((resolve, reject) => {
        if (typeof window.supersetEmbeddedSdk !== 'undefined') {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = superset_sdk_url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Superset SDK'));
        document.head.appendChild(script);
      });
    },

    async configureSupersetSDK() {
      if (!window.supersetEmbeddedSdk) throw new Error('Superset SDK not loaded');
      this.sdkConfigured = true;
    },

    // ========== DASHBOARD EMBEDDING ==========
    async embedDashboard(containerId, dashboardId, forceRefreshToken = true) {
      try {
        this.iframeLoading = true;
        this.iframeError = false;

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        // Clear cached token if forcing refresh
        if (forceRefreshToken) {
          TOKEN_CACHE.tokens.delete(dashboardId);
        }

        const guestTokenData = await this.fetchGuestToken(dashboardId, {
          forceRefresh: forceRefreshToken
        });

        if (!window.supersetEmbeddedSdk) {
          await this.initializeSupersetSDK();
          await this.configureSupersetSDK();
        }

        await window.supersetEmbeddedSdk.embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_CONFIG.baseUrl,
          mountPoint: mountPoint,
          fetchGuestToken: async () => {
            const tokenData = await this.fetchGuestToken(dashboardId, {
              allowMainDashboardFallback: true
            });
            return tokenData.token || guestTokenData.token;
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: false, expanded: false }
          }
        });

        // Wait until Superset iframe signals it's ready
        window.addEventListener("message", event => {
          if (event.data?.type === "dashboardLoadComplete" ||
            event.data?.event === "dashboard_loaded") {

            // Hide loader if still visible
            if (this.is_loading) {
              this.is_loading = false;
            }
          }
        });

        this.iframeLoading = false;
        this.embeddedDashboards.add(containerId);
      } catch (error) {
        console.error('Failed to embed dashboard:', error);
        this.iframeLoading = false;
        this.iframeError = true;
      }
    },

    cleanupDashboards() {
      const containers = ['dashboard-main', 'dashboard-reports', 'dashboard-analytics'];
      containers.forEach(containerId => {
        const container = document.getElementById(containerId);
        if (container) container.innerHTML = '';
      });
      this.embeddedDashboards.clear();
    },

    // ========== NAVIGATION METHODS ==========
    handleBreadcrumbClick(breadcrumbId) {

      if (breadcrumbId === 'impact-calculator') {
        this.currentView = 'impact-calculator';
        this.active_menu_item = 'impact-calculator';
        this.cleanupDashboards();
        return;
      }
      if (breadcrumbId === 'main') {
        // Clicked on "Portfolio Dashboard" - go back to global view
        this.goBack();
      } else {
        // Find the clicked breadcrumb item
        const clickedCrumb = this.breadcrumb.find(crumb => crumb.id === breadcrumbId);
        if (!clickedCrumb) return;

        const index = this.breadcrumb.findIndex(crumb => crumb.id === breadcrumbId);

        // If we clicked on the last item (current view), do nothing
        if (index === this.breadcrumb.length - 1) return;

        // Navigate to the clicked property
        const tile = this.findTile(breadcrumbId);
        if (tile) {
          this.selected_property = tile;
          this.current_dashboard_type = 'property';

          // Default to executive summary when navigating via breadcrumb
          this.active_menu_item = 'executive-overview';
          this.currentView = breadcrumbId;

          // Trim breadcrumb to only show up to clicked item
          this.breadcrumb = this.breadcrumb.slice(0, index + 1);

          this.$nextTick(() => {
            this.embedDashboard(`dashboard-${breadcrumbId}`, this.current_dashboard_id);
          });
        }
      }
    },

    async goBack() {
      // Clear token cache for property dashboards
      this.cleanupPropertyTokenCache();

      this.currentView = 'main';
      this.breadcrumb = [{ id: 'main', title: MAIN_BREADCRUMB_TITLE }];
      this.cleanupDashboards();
      this.current_dashboard_type = 'global';
      this.active_menu_item = 'executive-overview';
      this.selected_property = null;

      // Force refresh token for main dashboard
      TOKEN_CACHE.clear(); // Clear all cached tokens

      this.$nextTick(async () => {
        try {
          // Force fresh token fetch for main dashboard
          await this.embedDashboard('dashboard-main', this.current_dashboard_id);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    // Helper to clean property-specific tokens
    cleanupPropertyTokenCache() {
      if (!this.selected_property) return;

      const propertyName = String(this.selected_property.title || "");

      const dashboardsObj = this.property_specific_dashboards || {};
      const entries = Object.entries(dashboardsObj);

      entries.forEach(([name, propertyDashboards]) => {
        if (name !== propertyName) return;
        if (!propertyDashboards) return;

        Object.values(propertyDashboards).forEach((dashboardId) => {
          TOKEN_CACHE.tokens.delete(dashboardId);
        });
      });
    },

    async handleGlobalMenuSelected(menuItem) {
      // ✅ CALCULATOR (custom screen, not superset)
      if (this.isCustomView(menuItem)) {
        this.active_menu_item = menuItem;
        this.currentView = menuItem;             // "calculator"
        this.current_dashboard_type = 'global';  // keep as global (or set 'custom' if you prefer)
        this.selected_property = null;

        // stop any iframe loaders / cleanup superset containers
        this.iframeLoading = false;
        this.iframeError = false;
        this.cleanupDashboards();

        // breadcrumb
        this.breadcrumb = [
          { id: 'main', title: MAIN_BREADCRUMB_TITLE },
          { id: menuItem, title: 'Calculator' }
        ];

        return;
      }

      // ✅ NORMAL GLOBAL SUPERSET FLOW (unchanged)
      this.active_menu_item = menuItem;
      this.current_dashboard_type = 'global';
      this.currentView = 'main';
      this.selected_property = null;

      TOKEN_CACHE.cleanup();

      if (menuItem === 'executive-overview') {
        this.breadcrumb = [{ id: 'main', title: MAIN_BREADCRUMB_TITLE }];
      } else {
        this.breadcrumb = [
          { id: 'main', title: MAIN_BREADCRUMB_TITLE },
          { id: menuItem, title: this.formatBreadcrumbTitle(menuItem) }
        ];
      }

      this.$nextTick(async () => {
        try {
          await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load global dashboard:', error);
        }
      });
    },

    handlePropertyMenuSelected(data) {
      this.active_menu_item = data.menuItem;
      this.current_dashboard_type = 'property';

      const tile = this.findTile(data.propertyId);
      if (!tile) return;

      this.selected_property = tile;
      this.currentView = data.propertyId;

      this.breadcrumb = [
        { id: 'main', title: MAIN_BREADCRUMB_TITLE },
        { id: data.propertyId, title: `${data.propertyName}` }
      ];

      this.$nextTick(async () => {
        await this.embedDashboard(`dashboard-${data.propertyId}`, this.current_dashboard_id);

        if (data.menuItem === 'operations-overview') {
          await new Promise(resolve => setTimeout(resolve, 800));
          await this.$nextTick();

          if (this.$refs.emergencyMapRef) {
            try {
              await this.$refs.emergencyMapRef.ensureReady(8000);
            } catch (err) {
              console.warn('Emergency map init timed out:', err);
            }
          }
        }
      });
    },



    async handleTileClick(tileId) {
      const tile = this.findTile(tileId);
      if (!tile) return;

      this.selected_property = tile;
      this.current_dashboard_type = 'property';
      this.active_menu_item = 'operations-overview';

      // Set breadcrumb with proper structure
      this.breadcrumb = [
        { id: 'main', title: MAIN_BREADCRUMB_TITLE },
        { id: tileId, title: tile.title }
      ];

      this.currentView = tileId;
      this.iframeLoading = true;
      this.iframeError = false;

      const containerId = `dashboard-${tileId}`;
      const dashboardId = this.current_dashboard_id;

      try {
        // Clear any cached tokens for this property dashboard
        TOKEN_CACHE.tokens.delete(dashboardId);

        this.$nextTick(async () => {
          await this.embedDashboard(containerId, dashboardId);
        });
      } catch (error) {
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    // ========== HELPER METHODS ==========
    findTile(tileId) {
      const search = (arr) => {
        for (let item of arr) {
          if (item.id === tileId) return item;
          if (item.children) {
            const found = search(item.children);
            if (found) return found;
          }
          if (item.tileCards && Array.isArray(item.tileCards)) {
            const foundInTileCards = search(item.tileCards);
            if (foundInTileCards) return foundInTileCards;
          }
        }
        return null;
      };
      return search(this.tiles);
    },

    getTileCards() {
      // If we're in a property view and have selected property
      if (this.is_property_view && this.selected_property) {
        return this.selected_property.tileCards || [];
      }
      return [];
    },

    hasTileCards() {
      return this.is_property_view && this.selected_property &&
        this.selected_property.tileCards &&
        this.selected_property.tileCards.length > 0;
    },

    getCurrentTitle() {
      const tile = this.findTile(this.currentView);
      return tile ? tile.title : 'Overview';
    },

    isDetailView(tileId) {
      return tileId.includes('-') && tileId !== 'main';
    },

    // ========== UI HELPERS ==========
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.isMobile = window.innerWidth < 992;
      if (this.isMobile) this.rightPanelCollapsed = false;
    },

    getMainContentClass() {
      if (this.isMobile) return 'col-12';
      return this.rightPanelCollapsed ? 'col-12' : 'col-lg-12 col-md-12 m-0 p-0';
    },

    getChildrenContentClass() {
      return ''; // no bootstrap column classes
    },

    getChildrenSidebarClass() {
      return ''; // no bootstrap column classes
    },


    toggleRightPanel() {
      if (this.isMobile) return;
      this.rightPanelCollapsed = !this.rightPanelCollapsed;
    },

    getPropertyMunicipality() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality - Ganyesa';

        if (title.includes('Lesedi'))
          return 'City of Ekurhuleni (CoE) - Vosloorus';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality - Kenton-On-Sea';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town - Cape Town';

        // ---- placeholders (data later) ----
        if (title.includes('Erica')) return 'Municipality - TBD';
        if (title.includes('Amsterdam')) return 'Municipality - TBD';
        if (title.includes('Madadeni')) return 'Municipality - TBD';
        if (title.includes('Aliwal North')) return 'Municipality - TBD';
        if (title.includes('Rustenburg')) return 'Municipality - TBD';
        if (title.includes('Ngqamakhwe')) return 'Municipality - TBD';
        if (title.includes('Mkhondo')) return 'Municipality - TBD';
      }

      return 'Municipality';
    },

    getPropertyWaterUtility() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (title.includes('Lesedi'))
          return 'Ekurhuleni Water';

        if (title.includes('Thembokwezi'))
          return 'City of Cape Town Water';

        if (title.includes('Ekuphumleni'))
          return 'Ndlambe Local Municipality Water';

        if (title.includes('Ganyesa'))
          return 'Kagisano/Molopo Local Municipality Water';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Water Utility - TBD';
        if (title.includes('Amsterdam')) return 'Water Utility - TBD';
        if (title.includes('Madadeni')) return 'Water Utility - TBD';
        if (title.includes('Aliwal North')) return 'Water Utility - TBD';
        if (title.includes('Rustenburg')) return 'Water Utility - TBD';
        if (title.includes('Ngqamakhwe')) return 'Water Utility - TBD';
        if (title.includes('Mkhondo')) return 'Water Utility - TBD';
      }

      return 'Water Utility';
    },

    getPropertyTariffRegime() {
      if (this.selected_property) {
        const title = this.selected_property.title || '';

        if (
          title.includes('Lesedi') ||
          title.includes('Ekuphumleni') ||
          title.includes('Thembokwezi')
        ) {
          return 'TIME OF USE';
        }

        if (title.includes('Ganyesa'))
          return 'Tariff Regime - TBD';

        // ---- placeholders ----
        if (title.includes('Erica')) return 'Tariff Regime - TBD';
        if (title.includes('Amsterdam')) return 'Tariff Regime - TBD';
        if (title.includes('Madadeni')) return 'Tariff Regime - TBD';
        if (title.includes('Aliwal North')) return 'Tariff Regime - TBD';
        if (title.includes('Rustenburg')) return 'Tariff Regime - TBD';
        if (title.includes('Ngqamakhwe')) return 'Tariff Regime - TBD';
        if (title.includes('Mkhondo')) return 'Tariff Regime - TBD';
      }

      return 'Tariff Information';
    }
  }
};
